import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { ITEMS, type ItemId } from '@goodtime/shared';
import { TweenGroup } from '../../core/Tween';

/**
 * Player's loadout strip — one tile per unique item the local
 * player owns. During STAKE one tap on a tile submits DEPLOY_ITEM.
 * A spinner overlay covers the tile during the server round-trip,
 * and a panel above the strip names the item and its effect for a
 * few seconds.
 *
 * <p>Producer 2026-10-06: the old two-tap commit (first tap armed the
 * tile with a small "READY · TAP TO USE" label, second tap fired) was
 * confusing, so a single tap now uses the item. The armed state is kept
 * for hosts that still call {@link resetArmedAndFiring} but taps no
 * longer set it.
 *
 * <p>Producer brief 2026-05-16 — the strip was previously a React
 * component; the full Pixi port pulls it onto the canvas
 * alongside the disc + outcome chips so the wheel view is a
 * single render tree.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';
const TILE_SIZE = 56;
const TILE_GAP = 10;
const STRIP_PAD = 16;
const DESC_PANEL_GAP = 18;
const DESC_PANEL_MAX_WIDTH = 320;
/** How long the used item's name + effect stays up after a tap. */
const DESC_SHOW_MS = 3200;

export interface DeployStripTileData {
  itemId: ItemId;
  iconTexture: Texture | null;
  /** Total count of this item in the loadout. */
  owned: number;
  /** Number consumed via pendingDeployments. */
  deployed: number;
}

export interface DeployStripCallbacks {
  /** Fires when a tile is tapped to deploy its item.
   *  The host calls submitWheelCommand(DEPLOY_ITEM, itemId). */
  onFire: (itemId: ItemId) => void;
}

interface TileNode {
  itemId: ItemId;
  container: Container;
  bg: Graphics;
  iconSprite: Sprite | null;
  fallbackText: Text | null;
  countBadge: Container;
  countText: Text;
  armedRing: Graphics;
  spinnerHost: Container;
  spinnerArc: Graphics;
  spinnerDim: Graphics;
  readyLabel: Text;
}

export class WheelResolveDeployStrip extends Container {
  private readonly stripHost: Container;
  /** Full-screen dim that catches outside-strip taps to cancel
   *  the armed state. Mounted at scene level by the host. */
  private readonly outsideDim: Graphics;
  private readonly descPanel: Container;
  private readonly descBg: Graphics;
  private readonly descName: Text;
  private readonly descBody: Text;
  private readonly tween = new TweenGroup();
  private callbacks: DeployStripCallbacks;
  private tiles: DeployStripTileData[] = [];
  private nodes: TileNode[] = [];
  /** Currently armed itemId — second tap fires; tap elsewhere
   *  clears. */
  private armedItem: ItemId | null = null;
  /** itemId currently firing — overlay covers the tile while we
   *  wait for the server SSE delta. Cleared either when the
   *  pendingDeployments echo arrives or after the 2s safety net. */
  private firingItem: ItemId | null = null;
  /** Item whose name + effect the description panel shows after it is
   *  used. Cleared by a timer (DESC_SHOW_MS). */
  private describedItem: ItemId | null = null;
  private describeTimer: number | null = null;
  /** Pixi ticker hook reservation for the spinner. */
  private spinnerElapsed = 0;
  /** Viewport bounds — used to size the outside-dim + lay out the
   *  strip + description. */
  private viewportWidth = 0;
  private viewportHeight = 0;

  constructor(callbacks: DeployStripCallbacks) {
    super();
    this.callbacks = callbacks;

    this.outsideDim = new Graphics();
    this.outsideDim.eventMode = 'static';
    this.outsideDim.cursor = 'pointer';
    this.outsideDim.visible = false;
    this.outsideDim.on('pointertap', () => this.setArmed(null));
    this.addChild(this.outsideDim);

    this.stripHost = new Container();
    this.stripHost.eventMode = 'static';
    this.stripHost.on('pointertap', (event) => {
      // Stop the dim's tap from firing too.
      event.stopPropagation();
    });
    this.addChild(this.stripHost);

    this.descPanel = new Container();
    this.descPanel.visible = false;
    this.descBg = new Graphics();
    this.descPanel.addChild(this.descBg);
    this.descName = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 11,
        fontWeight: '800',
        fill: 0xffd86b,
        letterSpacing: 1.2,
      },
    });
    this.descName.anchor.set(0.5, 0);
    this.descPanel.addChild(this.descName);
    this.descBody = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 12,
        fontWeight: '500',
        fill: 0xe9f0ff,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: DESC_PANEL_MAX_WIDTH - 24,
        lineHeight: 16,
      },
    });
    this.descBody.anchor.set(0.5, 0);
    this.descPanel.addChild(this.descBody);
    this.addChild(this.descPanel);
  }

  /** Host pushes the latest tile model on every state delta.
   *  Tiles persist by itemId across updates so per-tile animation
   *  state (firing spinner, armed ring) stays stable. */
  setTiles(
    tiles: ReadonlyArray<DeployStripTileData>,
    hasDeployedThisRound: boolean,
  ): void {
    this.tiles = tiles.slice();
    // Re-sync node pool to match tiles. Reuse existing nodes by
    // itemId — keeps the armed/firing ring states from flickering
    // on hot state pushes.
    const byId = new Map<ItemId, TileNode>();
    for (const node of this.nodes) byId.set(node.itemId, node);
    const nextNodes: TileNode[] = [];
    for (const tile of tiles) {
      const existing = byId.get(tile.itemId);
      if (existing) {
        byId.delete(tile.itemId);
        nextNodes.push(existing);
      } else {
        nextNodes.push(this.createNode(tile.itemId));
      }
    }
    // Destroy any unused nodes (loadout changed, item removed).
    for (const stale of byId.values()) {
      this.stripHost.removeChild(stale.container);
      stale.container.destroy({ children: true });
    }
    this.nodes = nextNodes;
    // If the firing item has cleared from pendingDeployments
    // (server confirmed), drop the spinner.
    if (this.firingItem !== null) {
      const tile = tiles.find((t) => t.itemId === this.firingItem);
      if (tile && tile.deployed > 0) {
        this.firingItem = null;
      }
    }
    this.refreshTileVisuals(hasDeployedThisRound);
    this.layoutStrip();
    this.refreshDescPanel();
  }

  /** Host pushes viewport size on every onResize so the strip can
   *  anchor near the bottom and the dim can fill the canvas. */
  layout(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.outsideDim.clear();
    this.outsideDim
      .rect(0, 0, width, height)
      .fill({ color: 0x060d22, alpha: 0.35 });
    this.layoutStrip();
    this.refreshDescPanel();
  }

  /** Drive the spinner animation off the host scene's ticker. */
  tick(deltaMs: number): void {
    if (this.firingItem === null) return;
    this.spinnerElapsed += deltaMs;
    const node = this.nodes.find((n) => n.itemId === this.firingItem);
    if (!node) return;
    node.spinnerHost.angle = (this.spinnerElapsed / 700) * 360;
  }

  /** Force-clear the armed + firing state — host calls this when
   *  the phase leaves STAKE so the strip is "clean" on the next
   *  STAKE entry. */
  resetArmedAndFiring(): void {
    this.armedItem = null;
    this.firingItem = null;
    this.describedItem = null;
    if (this.describeTimer !== null) {
      window.clearTimeout(this.describeTimer);
      this.describeTimer = null;
    }
    this.descPanel.visible = false;
    this.outsideDim.visible = false;
    this.refreshTileVisuals(false);
  }

  destroyDisplay(): void {
    this.tween.cancelAll();
    if (this.describeTimer !== null) window.clearTimeout(this.describeTimer);
    for (const node of this.nodes) node.container.destroy({ children: true });
    this.nodes = [];
    this.removeChildren();
  }

  // ---- internal ---------------------------------------------------------

  private createNode(itemId: ItemId): TileNode {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', (event) => {
      event.stopPropagation();
      this.onTileTap(itemId);
    });

    const bg = new Graphics();
    container.addChild(bg);

    const armedRing = new Graphics();
    armedRing.visible = false;
    container.addChild(armedRing);

    // Icon placeholder — populated on every refreshTileVisuals().
    const fallbackText = new Text({
      text: itemId.slice(0, 3),
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        fontWeight: '900',
        fill: 0xffd66b,
      },
    });
    fallbackText.anchor.set(0.5);
    container.addChild(fallbackText);

    const countBadge = new Container();
    countBadge.visible = false;
    container.addChild(countBadge);
    const countBg = new Graphics();
    countBadge.addChild(countBg);
    const countText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 10,
        fontWeight: '900',
        fill: 0x3a2700,
      },
    });
    countText.anchor.set(0.5);
    countBadge.addChild(countText);

    const spinnerHost = new Container();
    spinnerHost.visible = false;
    container.addChild(spinnerHost);
    const spinnerDim = new Graphics();
    spinnerHost.addChild(spinnerDim);
    const spinnerArc = new Graphics();
    spinnerHost.addChild(spinnerArc);

    const readyLabel = new Text({
      text: 'READY · TAP TO USE',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        fontWeight: '900',
        fill: 0xffe48a,
        letterSpacing: 1.4,
      },
    });
    readyLabel.anchor.set(0.5);
    readyLabel.visible = false;
    container.addChild(readyLabel);

    this.stripHost.addChild(container);
    return {
      itemId,
      container,
      bg,
      iconSprite: null,
      fallbackText,
      countBadge,
      countText,
      armedRing,
      spinnerHost,
      spinnerArc,
      spinnerDim,
      readyLabel,
    };
  }

  private refreshTileVisuals(hasDeployedThisRound: boolean): void {
    for (let i = 0; i < this.nodes.length; i++) {
      const node = this.nodes[i]!;
      const tile = this.tiles[i]!;
      const remaining = tile.owned - tile.deployed;
      const isDeployed = tile.deployed > 0;
      const usable = remaining > 0 && !hasDeployedThisRound;
      const isArmed = this.armedItem === tile.itemId;
      const isFiring = this.firingItem === tile.itemId;
      const isOtherArmed =
        this.armedItem !== null && this.armedItem !== tile.itemId;

      // Background fill.
      node.bg.clear();
      if (usable) {
        if (isArmed) {
          node.bg
            .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
            .fill({ color: 0xffe48a, alpha: 1 })
            .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
            .stroke({ color: 0xffffff, alpha: 1, width: 2 });
        } else {
          node.bg
            .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
            .fill({ color: 0x2a1e63, alpha: 1 })
            .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
            .stroke({ color: 0xffd66b, alpha: 1, width: 1.5 });
        }
      } else if (isDeployed) {
        node.bg
          .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
          .fill({ color: 0xffd96b, alpha: 1 })
          .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
          .stroke({ color: 0xffffff, alpha: 1, width: 1.5 });
      } else {
        node.bg
          .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
          .fill({ color: 0x140c28, alpha: 0.55 })
          .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
          .stroke({ color: 0xffffff, alpha: 0.18, width: 1.5 });
      }

      // Icon sprite — refresh when texture / availability changes.
      if (node.iconSprite) {
        node.container.removeChild(node.iconSprite);
        node.iconSprite.destroy();
        node.iconSprite = null;
      }
      if (tile.iconTexture) {
        const sprite = new Sprite(tile.iconTexture);
        sprite.anchor.set(0.5);
        sprite.width = TILE_SIZE - 12;
        sprite.height = TILE_SIZE - 12;
        sprite.eventMode = 'none';
        node.container.addChildAt(sprite, 2);
        node.iconSprite = sprite;
        node.fallbackText!.visible = false;
      } else {
        node.fallbackText!.visible = true;
        node.fallbackText!.text = tile.itemId.slice(0, 3);
      }

      // Count badge.
      if (remaining > 1) {
        node.countBadge.visible = true;
        node.countText.text = `×${remaining}`;
        // Recompute pill width based on the text content.
        const padX = 4;
        const badgeH = 18;
        const badgeW = Math.max(18, Math.ceil(node.countText.width) + padX * 2);
        const bg = node.countBadge.getChildAt(0) as Graphics;
        bg.clear();
        bg
          .roundRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, badgeH / 2)
          .fill({ color: 0xffd66b, alpha: 1 })
          .roundRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, badgeH / 2)
          .stroke({ color: 0x2a1e63, alpha: 1, width: 1.5 });
        node.countBadge.x = TILE_SIZE / 2 - 2;
        node.countBadge.y = TILE_SIZE / 2 - 2;
      } else {
        node.countBadge.visible = false;
      }

      // Tile elevation / dimming.
      node.container.alpha = isOtherArmed && !isArmed ? 0.4 : 1;
      node.container.y = isArmed ? -8 : isFiring ? -4 : 0;
      node.container.scale.set(isArmed ? 1.12 : isFiring ? 1.05 : 1);
      node.container.cursor = usable ? 'pointer' : 'default';
      node.container.eventMode = usable ? 'static' : 'none';

      // Armed pulse ring.
      if (isArmed) {
        node.armedRing.visible = true;
        node.armedRing.clear();
        node.armedRing
          .roundRect(
            -TILE_SIZE / 2 - 3,
            -TILE_SIZE / 2 - 3,
            TILE_SIZE + 6,
            TILE_SIZE + 6,
            17,
          )
          .stroke({ color: 0xffd66b, alpha: 0.85, width: 1.5 });
      } else {
        node.armedRing.visible = false;
      }

      // READY · TAP TO USE callout when armed.
      node.readyLabel.visible = isArmed;
      if (isArmed) {
        node.readyLabel.x = 0;
        node.readyLabel.y = -TILE_SIZE / 2 - 12;
      }

      // Firing spinner overlay.
      if (isFiring) {
        node.spinnerHost.visible = true;
        node.spinnerDim.clear();
        node.spinnerDim
          .roundRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14)
          .fill({ color: 0x0a0518, alpha: 0.55 });
        node.spinnerArc.clear();
        node.spinnerArc
          .arc(0, 0, 12, 0, Math.PI * 1.5)
          .stroke({ color: 0xffe48a, alpha: 1, width: 3 });
      } else {
        node.spinnerHost.visible = false;
      }
    }
  }

  private layoutStrip(): void {
    if (this.viewportWidth === 0) return;
    const n = this.nodes.length;
    if (n === 0) {
      this.stripHost.visible = false;
      return;
    }
    this.stripHost.visible = true;
    const totalW = n * TILE_SIZE + (n - 1) * TILE_GAP;
    const startX = this.viewportWidth / 2 - totalW / 2 + TILE_SIZE / 2;
    // Anchor 96px from bottom of viewport — clear of the bottom
    // seat avatar's orbital position.
    const y = this.viewportHeight - 96;
    this.stripHost.y = y;
    for (let i = 0; i < n; i++) {
      const node = this.nodes[i]!;
      node.container.x = startX + i * (TILE_SIZE + TILE_GAP);
    }
    void STRIP_PAD;
  }

  private refreshDescPanel(): void {
    const shown = this.describedItem ?? this.armedItem;
    if (shown === null) {
      this.descPanel.visible = false;
      return;
    }
    const meta = ITEMS[shown];
    if (!meta) {
      this.descPanel.visible = false;
      return;
    }
    this.descPanel.visible = true;
    this.descName.text = meta.name.toUpperCase();
    this.descBody.text = meta.description;
    // Lay out + draw bg sized to content.
    const innerW = Math.min(DESC_PANEL_MAX_WIDTH, this.viewportWidth - 32);
    const padTop = 8;
    const padBottom = 9;
    const totalH = Math.ceil(this.descName.height + 6 + this.descBody.height);
    this.descBg.clear();
    this.descBg
      .roundRect(
        -innerW / 2,
        -(totalH + padTop + padBottom) / 2,
        innerW,
        totalH + padTop + padBottom,
        12,
      )
      .fill({ color: 0x060d22, alpha: 0.78 })
      .roundRect(
        -innerW / 2,
        -(totalH + padTop + padBottom) / 2,
        innerW,
        totalH + padTop + padBottom,
        12,
      )
      .stroke({ color: 0xffd86b, alpha: 0.45, width: 1 });
    this.descName.x = 0;
    this.descName.y = -(totalH + padTop + padBottom) / 2 + padTop;
    this.descBody.x = 0;
    this.descBody.y = this.descName.y + this.descName.height + 4;
    this.descPanel.x = this.viewportWidth / 2;
    // Position the description panel ABOVE the tile strip — the
    // strip itself is anchored 96px above viewport bottom (line 448),
    // and the previous "below the strip" anchor pushed the panel
    // off-screen on shorter viewports. Sitting above the strip keeps
    // the full panel readable on every tier.
    this.descPanel.y =
      this.viewportHeight - 96 - TILE_SIZE / 2 - DESC_PANEL_GAP -
      (totalH + padTop + padBottom) / 2;
  }

  private onTileTap(itemId: ItemId): void {
    const tile = this.tiles.find((t) => t.itemId === itemId);
    if (!tile) return;
    const remaining = tile.owned - tile.deployed;
    const hasDeployed = this.tiles.some((t) => t.deployed > 0);
    const usable = remaining > 0 && !hasDeployed;
    if (!usable) return;
    this.fireDeploy(itemId);
  }

  private setArmed(itemId: ItemId | null): void {
    this.armedItem = itemId;
    this.outsideDim.visible = itemId !== null;
    this.refreshTileVisuals(this.tiles.some((t) => t.deployed > 0));
    this.refreshDescPanel();
  }

  private fireDeploy(itemId: ItemId): void {
    this.firingItem = itemId;
    this.armedItem = null;
    this.outsideDim.visible = false;
    this.spinnerElapsed = 0;
    this.refreshTileVisuals(true);
    // Name the item and what it does, so a one-tap use still tells
    // the player what happened.
    this.describedItem = itemId;
    if (this.describeTimer !== null) window.clearTimeout(this.describeTimer);
    this.describeTimer = window.setTimeout(() => {
      this.describeTimer = null;
      this.describedItem = null;
      this.refreshDescPanel();
    }, DESC_SHOW_MS);
    this.refreshDescPanel();
    this.callbacks.onFire(itemId);
    // Safety-net timeout: if the SSE delta never lands, drop the
    // spinner after 2 seconds.
    window.setTimeout(() => {
      if (this.firingItem === itemId) {
        this.firingItem = null;
        this.refreshTileVisuals(this.tiles.some((t) => t.deployed > 0));
      }
    }, 2000);
  }
}
